import React, { useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Spinner } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

// Local type definitions for button styling
interface ControlVariantStyles {
  bg: string;
  bgHover: string;
  bgPress: string;
  bgDisable?: string;
  border: string;
  borderHover: string;
  borderPress: string;
  borderDisable?: string;
}

interface ControlTextStyles {
  primary: string;
  secondary: string;
  tertiary: string;
  ghost: string;
  disable: string;
}

interface ButtonSizeStyles {
  height: number;
  padding: number;
  fontSize: number;
  lineHeight: number;
  sublabelFontSize: number;
  spinnerSize: number;
  iconSize: number;
  iconContainerSize: number;
  borderRadius: number;
  labelIconSize: number;
  labelIconGap: number;
}

/**
 * Helper to clone an icon element with default props.
 * Only applies size/color if not already specified by the user.
 * This allows users to override the default size: <ArrowRight size={16} />
 */
const cloneIconWithDefaults = (
  icon: React.ReactNode,
  defaultSize: number,
  defaultColor: string
): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon;

  const iconElement = icon as React.ReactElement<any>;
  const existingProps = iconElement.props || {};

  return React.cloneElement(iconElement, {
    ...existingProps, // Preserve existing props (including accessibility attributes)
    size: existingProps.size ?? defaultSize,
    color: existingProps.color ?? defaultColor,
  });
};

export interface ButtonProps extends ThemeOverrideProps {
  /** Visual variant of the button */
  variant?: "primary" | "secondary" | "tertiary" | "ghost";
  /** Color tone of the button */
  tone?: "brand" | "brandExtra" | "alert" | "mono";
  /** Size of the button */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Button content */
  children: React.ReactNode;
  /**
   * Activation handler. Invoked on click, or on Enter/Space when the button is
   * focused. Not called while `disabled` or `loading`.
   */
  onPress?: () => void;
  /**
   * Icon to display on the left side.
   * Size and color are automatically set based on button size/state.
   * To override, specify size/color on the icon: `iconLeft={<ArrowLeft size={16} />}`
   */
  iconLeft?: React.ReactNode;
  /**
   * Icon to display on the right side.
   * Size and color are automatically set based on button size/state.
   * To override, specify size/color on the icon: `iconRight={<ArrowRight size={16} />}`
   */
  iconRight?: React.ReactNode;
  /** Secondary text displayed inline with the main label (e.g., price), shown with 40% opacity */
  sublabel?: string;
  /** Alignment of the label text */
  labelAlignment?: "left" | "center";
  /**
   * Small icon displayed directly to the left of the label text.
   * Size and color are automatically set based on button size/state.
   * To override, specify size/color on the icon: `labelIcon={<InfoIcon size={12} />}`
   */
  labelIcon?: React.ReactNode;
  /**
   * Small icon displayed directly to the right of the label text.
   * Size and color are automatically set based on button size/state.
   * To override, specify size/color on the icon: `labelIconRight={<InfoIcon size={12} />}`
   */
  labelIconRight?: React.ReactNode;
  /** Custom content slot for badges, tags, or other elements */
  customContent?: React.ReactNode;
  /** Accessible label for screen readers (use for icon-only buttons) */
  "aria-label"?: string;
  /** ID of element that describes this button */
  "aria-describedby"?: string;
  /** Indicates the button controls an expandable element */
  "aria-expanded"?: boolean;
  /** Indicates the type of popup triggered by the button */
  "aria-haspopup"?: boolean | "menu" | "listbox" | "tree" | "grid" | "dialog";
  /** Indicates the button is pressed (for toggle buttons) */
  "aria-pressed"?: boolean | "mixed";
  /** ID of the element this button controls */
  "aria-controls"?: string;
  testID?: string;
  id?: string;
  /** HTML type attribute for the button */
  type?: "button" | "submit" | "reset";
  /** Whether the button should stretch to fill the full width of its container */
  fullWidth?: boolean;
}

/**
 * Button - An accessible button component
 *
 * Renders as a semantic `<button>` element with full ARIA support.
 * Supports various visual variants, sizes, and states including loading.
 *
 * ## Accessibility Features
 *
 * - **Semantic HTML**: Renders as a native `<button>` element
 * - **Keyboard Navigation**: Focusable via Tab, activated with Enter or Space
 * - **ARIA States**: Properly announces disabled and loading states
 * - **Focus Indicator**: Visible focus ring for keyboard navigation
 * - **Screen Reader Support**: Announces button label, state, and any associated descriptions
 *
 */
export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  tone = "brand",
  size = "md",
  disabled = false,
  loading = false,
  children,
  onPress,
  iconLeft,
  iconRight,
  sublabel,
  labelAlignment = "center",
  labelIcon,
  labelIconRight,
  customContent,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  "aria-haspopup": ariaHasPopup,
  "aria-pressed": ariaPressed,
  "aria-controls": ariaControls,
  testID,
  id,
  type = "button",
  fullWidth = false,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const [isKeyboardPressed, setIsKeyboardPressed] = useState(false);

  const isDisabled = disabled || loading;

  // Type assertion for extended sizing properties
  const sizeStyles = theme.sizing.button(size) as ButtonSizeStyles;

  // Type assertion for control variant styles - the JSON tokens include tertiary
  const controlTone = theme?.colors?.control?.[tone] as unknown as
    | (Record<string, ControlVariantStyles> & { text: ControlTextStyles })
    | undefined;

  const variantStyles: ControlVariantStyles = controlTone?.[variant] ||
    (
      theme?.colors?.control?.brand as unknown as Record<
        string,
        ControlVariantStyles
      >
    )?.primary || {
      bg: "transparent",
      bgHover: "transparent",
      bgPress: "transparent",
      bgDisable: "transparent",
      border: "transparent",
      borderHover: "transparent",
      borderPress: "transparent",
      borderDisable: "transparent",
    };

  // Text colors are at the tone level, mapped by variant name
  const textStyles: ControlTextStyles = controlTone?.text ||
    (theme?.colors?.control?.brand as unknown as { text: ControlTextStyles })
      ?.text || {
      primary: "#000",
      secondary: "#fff",
      tertiary: "#888",
      ghost: "#000",
      disable: "#666",
    };

  // Uses the aria-disabled pattern (not the native `disabled` attribute) so the
  // button stays focusable and can surface a tooltip explaining why it is
  // unavailable. Activation must therefore be blocked manually: preventDefault on
  // the triggering event stops form submission / default button behaviour.
  const handlePress = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      e?.preventDefault();
      return;
    }
    if (onPress) {
      onPress();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      // Block native activation while disabled, keeping the element focusable.
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      setIsKeyboardPressed(true);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (isDisabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsKeyboardPressed(false);
      if (onPress) {
        onPress();
      }
    }
  };

  // Disabled state is identical across all variants and tones. Figma maps every
  // disabled button to the canonical brand tokens (control/brand/primary/bg-disable,
  // control/brand/primary/border-disable, control/brand/text/disable), so resolve
  // them from brand regardless of the active variant/tone.
  const brandControl = theme?.colors?.control?.brand as unknown as
    | (Record<string, ControlVariantStyles> & { text: ControlTextStyles })
    | undefined;
  const disabledBg =
    brandControl?.primary?.bgDisable ??
    variantStyles.bgDisable ??
    variantStyles.bg;
  const disabledBorder =
    brandControl?.primary?.borderDisable ??
    variantStyles.borderDisable ??
    variantStyles.border;
  const disabledText = brandControl?.text?.disable ?? textStyles.disable;

  let backgroundColor = variantStyles.bg;
  if (disabled) {
    backgroundColor = disabledBg;
  } else if (isKeyboardPressed) {
    backgroundColor = variantStyles.bgPress || variantStyles.bg;
  }

  // Border color tracks state alongside the background. Hover/press are applied
  // via hoverStyle/pressStyle below; the keyboard-activated press is resolved here.
  let borderColor = variantStyles.border;
  if (disabled) {
    borderColor = disabledBorder;
  } else if (isKeyboardPressed) {
    borderColor = variantStyles.borderPress || variantStyles.border;
  }

  // Text/icon color shares the single disabled color when disabled
  const textColor = disabled ? disabledText : textStyles[variant];

  // Only use aria-label when explicitly provided
  // Text content provides accessible name naturally for buttons with visible text
  const computedAriaLabel = ariaLabel;

  return (
    <Box
      as="button"
      type={type}
      id={id}
      onPress={handlePress}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      aria-label={computedAriaLabel}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-pressed={ariaPressed}
      aria-controls={ariaControls}
      testID={testID}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={
        borderColor !== "transparent" &&
        borderColor !== "rgba(255, 255, 255, 0)"
          ? 1
          : 0
      }
      borderRadius={sizeStyles.borderRadius}
      height={sizeStyles.height}
      width={fullWidth ? "100%" : undefined}
      padding={0}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      position="relative"
      cursor={disabled ? "not-allowed" : loading ? "wait" : "pointer"}
      style={{
        transition: "background-color 500ms ease, border-color 500ms ease",
        opacity: 1,
      }}
      hoverStyle={
        !isDisabled
          ? {
              backgroundColor: variantStyles?.bgHover,
              borderColor: variantStyles?.borderHover,
            }
          : undefined
      }
      pressStyle={
        !isDisabled
          ? {
              backgroundColor: variantStyles?.bgPress,
              borderColor: variantStyles?.borderPress,
            }
          : undefined
      }
      focusStyle={{
        outlineColor: theme.colors.border.brand,
        outlineWidth: 2,
        outlineOffset: 2,
        outlineStyle: "solid",
      }}
    >
      {/* Loading Spinner - Absolutely positioned in center */}
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
          zIndex={1}
        >
          <Spinner
            color={textColor}
            size={sizeStyles.spinnerSize}
            aria-hidden={true}
          />
        </Box>
      )}

      {/* Left Icon Section - Square container matching button height */}
      {iconLeft && (
        <Box
          width={sizeStyles.iconContainerSize}
          height={sizeStyles.iconContainerSize}
          alignItems="center"
          justifyContent="center"
          aria-hidden={true}
          style={{
            opacity: loading ? 0 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {cloneIconWithDefaults(iconLeft, sizeStyles.iconSize, textColor)}
        </Box>
      )}

      {/* Center Section: Content Area */}
      <Box
        flex={fullWidth ? 1 : undefined}
        flexDirection="row"
        alignItems="center"
        justifyContent={labelAlignment === "left" ? "flex-start" : "center"}
        paddingHorizontal={sizeStyles.padding}
        height="100%"
        gap={sizeStyles.labelIconGap}
        style={{
          opacity: loading ? 0 : 1,
          pointerEvents: loading ? "none" : "auto",
        }}
        aria-hidden={loading ? true : undefined}
      >
        {/* Label Icon (left of text) */}
        {labelIcon && (
          <Box aria-hidden={true}>
            {cloneIconWithDefaults(
              labelIcon,
              sizeStyles.labelIconSize,
              textColor
            )}
          </Box>
        )}

        {/* Label */}
        <Text
          color={textColor}
          fontSize={sizeStyles.fontSize}
          lineHeight={sizeStyles.lineHeight}
          fontWeight="500"
        >
          {children}
        </Text>

        {/* Label Icon (right of text) */}
        {labelIconRight && (
          <Box aria-hidden={true}>
            {cloneIconWithDefaults(
              labelIconRight,
              sizeStyles.labelIconSize,
              textColor
            )}
          </Box>
        )}

        {/* Sublabel - inline with label, 40% opacity as per Figma */}
        {sublabel && (
          <Text
            color={textColor}
            fontSize={sizeStyles.sublabelFontSize}
            lineHeight={sizeStyles.lineHeight}
            fontWeight="500"
            style={{ opacity: 0.4 }}
          >
            {sublabel}
          </Text>
        )}

        {/* Custom Content Slot */}
        {customContent && <Box aria-hidden={true}>{customContent}</Box>}
      </Box>

      {/* Right Icon Section - Square container matching button height */}
      {iconRight && (
        <Box
          width={sizeStyles.iconContainerSize}
          height={sizeStyles.iconContainerSize}
          alignItems="center"
          justifyContent="center"
          aria-hidden={true}
          style={{
            opacity: loading ? 0 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {cloneIconWithDefaults(iconRight, sizeStyles.iconSize, textColor)}
        </Box>
      )}
    </Box>
  );
};

Button.displayName = "Button";
